import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Form,
    Modal,
    Nav,
    Spinner,
    Table,
} from 'react-bootstrap';
import Select from 'react-select';
import {
    createSynapseItem,
    deleteSynapseItem,
    getSynapseDiscussions,
    getSynapseOverview,
    SynapseDiscussionOption,
    SynapseEntityItem,
    SynapseListKey,
    SynapseOverview,
    SynapsePartner,
    SynapseProject,
    SynapseSession,
    SynapseTeam,
    SynapseTeamRef,
    SynapseUser,
    updateSynapseItem,
} from './api';

type EntityKey = SynapseListKey;

type SynapseData = SynapseOverview;

type FormState = {
    id?: string;
    prim?: string | number;
    name?: string;
    email?: string;
    partnerid?: string;
    profilepic?: string;
    password?: string;
    logourl?: string;
    description?: string;
    bannerurl?: string;
    project_id?: string;
    session_id?: string;
    teams?: string[];
};

type SelectOption = {
    value: string;
    label: string;
    partnerid?: string | null;
};

const DEFAULT_PROFILE_PIC =
    'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500';

const EMPTY_DATA: SynapseData = {
    partners: [],
    teams: [],
    projects: [],
    sessions: [],
    users: [],
};

const TAB_LABELS: { key: EntityKey; label: string }[] = [
    { key: 'users', label: 'Users' },
    { key: 'teams', label: 'Teams' },
    { key: 'projects', label: 'Projects' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'partners', label: 'Partners' },
];

const emptyFormState = (entity: EntityKey): FormState => {
    switch (entity) {
        case 'partners':
            return { id: '', name: '', logourl: '' };
        case 'teams':
            return { id: '', name: '', partnerid: '', logourl: '' };
        case 'projects':
            return {
                id: '',
                name: '',
                description: '',
                bannerurl: '',
                teams: [],
            };
        case 'sessions':
            return { prim: '', project_id: '', session_id: '' };
        case 'users':
            return {
                id: '',
                name: '',
                email: '',
                partnerid: '',
                profilepic: DEFAULT_PROFILE_PIC,
                password: '',
                teams: [],
            };
    }
};

const titleCase = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1);

const asArray = <T,>(value: unknown): T[] =>
    Array.isArray(value) ? (value as T[]) : [];

const asTeamRefs = (value: unknown): SynapseTeamRef[] =>
    asArray<SynapseTeamRef>(value);

const formatDate = (value: unknown) => {
    if (!value) return '-';
    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

const Thumb: React.FC<{
    src?: string | null;
    alt: string;
    width?: number;
    height?: number;
}> = ({ src, alt, width = 40, height = 40 }) => {
    if (!src) return <span>-</span>;
    return (
        <img
            src={src}
            alt={alt}
            style={{
                width,
                height,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #dee2e6',
                background: '#f8f9fa',
            }}
        />
    );
};

const ImagePreview: React.FC<{ src?: string; label?: string }> = ({
    src,
    label = 'Preview',
}) => {
    if (!src?.trim()) return null;
    return (
        <div className="mt-2">
            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 4 }}>
                {label}
            </div>
            <Thumb src={src} alt={label} width={80} height={80} />
        </div>
    );
};

const SynapseManagement: React.FC = () => {
    const [activeTab, setActiveTab] = useState<EntityKey>('users');
    const [data, setData] = useState<SynapseData>(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingItem, setEditingItem] = useState<SynapseEntityItem | null>(
        null,
    );
    const [formState, setFormState] = useState<FormState>(
        emptyFormState('users'),
    );
    const [itemToDelete, setItemToDelete] = useState<SynapseEntityItem | null>(
        null,
    );
    const [discussions, setDiscussions] = useState<SynapseDiscussionOption[]>(
        [],
    );
    const [discussionsLoading, setDiscussionsLoading] = useState(false);
    const [filterPartnerId, setFilterPartnerId] = useState('');
    const [filterTeamId, setFilterTeamId] = useState('');
    const [filterProjectId, setFilterProjectId] = useState('');

    const resetFilters = () => {
        setFilterPartnerId('');
        setFilterTeamId('');
        setFilterProjectId('');
    };

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const overview = await getSynapseOverview();
            setData({
                partners: asArray<SynapsePartner>(overview.partners),
                teams: asArray<SynapseTeam>(overview.teams),
                projects: asArray<SynapseProject>(overview.projects),
                sessions: asArray<SynapseSession>(overview.sessions),
                users: asArray<SynapseUser>(overview.users),
            });
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load Synapse management data',
            );
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDiscussions = useCallback(async () => {
        setDiscussionsLoading(true);
        try {
            const rows = await getSynapseDiscussions();
            setDiscussions(asArray<SynapseDiscussionOption>(rows));
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load active discussions',
            );
        } finally {
            setDiscussionsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    useEffect(() => {
        if (showFormModal && activeTab === 'sessions') {
            fetchDiscussions();
        }
    }, [showFormModal, activeTab, fetchDiscussions]);

    const partnerOptions: SelectOption[] = useMemo(
        () =>
            data.partners.map(partner => ({
                value: partner.id,
                label: partner.name || partner.id,
            })),
        [data.partners],
    );

    const teamOptions: SelectOption[] = useMemo(
        () =>
            data.teams.map(team => ({
                value: team.id,
                label: `${team.name || team.id}${
                    team.partner_name ? ` (${team.partner_name})` : ''
                }`,
                partnerid: team.partnerid,
            })),
        [data.teams],
    );

    const projectOptions: SelectOption[] = useMemo(
        () =>
            data.projects.map(project => ({
                value: project.id,
                label: project.name || project.id,
            })),
        [data.projects],
    );

    const discussionOptions = useMemo(() => {
        const options: SelectOption[] = discussions.map(discussion => ({
            value: discussion.discussionid,
            label: `${discussion.name || 'Untitled'} (${
                discussion.status || 'n/a'
            })`,
        }));

        const editingSession =
            editingItem && 'session_id' in editingItem
                ? (editingItem as SynapseSession)
                : null;

        if (
            formState.session_id &&
            !options.some(option => option.value === formState.session_id)
        ) {
            const fallbackName =
                editingSession?.session_name || formState.session_id;
            const fallbackStatus = editingSession?.session_status || 'linked';
            options.unshift({
                value: formState.session_id,
                label: `${fallbackName} (${fallbackStatus})`,
            });
        }

        return options;
    }, [discussions, editingItem, formState.session_id]);

    const filteredTeamOptions = useMemo(() => {
        if (activeTab === 'users' && formState.partnerid) {
            return teamOptions.filter(
                team => team.partnerid === formState.partnerid,
            );
        }

        return teamOptions;
    }, [activeTab, formState.partnerid, teamOptions]);

    const filterTeamOptions = useMemo(() => {
        if (filterPartnerId) {
            return teamOptions.filter(
                team => team.partnerid === filterPartnerId,
            );
        }
        return teamOptions;
    }, [filterPartnerId, teamOptions]);

    const filteredUsers = useMemo(() => {
        return data.users.filter(user => {
            if (filterPartnerId && user.partnerid !== filterPartnerId) {
                return false;
            }
            if (
                filterTeamId &&
                !asTeamRefs(user.teams).some(team => team.id === filterTeamId)
            ) {
                return false;
            }
            return true;
        });
    }, [data.users, filterPartnerId, filterTeamId]);

    const filteredTeams = useMemo(() => {
        if (!filterPartnerId) return data.teams;
        return data.teams.filter(team => team.partnerid === filterPartnerId);
    }, [data.teams, filterPartnerId]);

    const filteredProjects = useMemo(() => {
        if (!filterTeamId) return data.projects;
        return data.projects.filter(project =>
            asTeamRefs(project.teams).some(team => team.id === filterTeamId),
        );
    }, [data.projects, filterTeamId]);

    const filteredSessions = useMemo(() => {
        if (!filterProjectId) return data.sessions;
        return data.sessions.filter(
            session => session.project_id === filterProjectId,
        );
    }, [data.sessions, filterProjectId]);

    const filteredRowsCount = (() => {
        switch (activeTab) {
            case 'users':
                return filteredUsers.length;
            case 'teams':
                return filteredTeams.length;
            case 'projects':
                return filteredProjects.length;
            case 'sessions':
                return filteredSessions.length;
            default:
                return data.partners.length;
        }
    })();

    const hasActiveFilters =
        (activeTab === 'users' && (!!filterPartnerId || !!filterTeamId)) ||
        (activeTab === 'teams' && !!filterPartnerId) ||
        (activeTab === 'projects' && !!filterTeamId) ||
        (activeTab === 'sessions' && !!filterProjectId);

    const openCreateModal = () => {
        setEditingItem(null);
        setFormState(emptyFormState(activeTab));
        setShowFormModal(true);
        setError('');
        setSuccess('');
    };

    const openEditModal = (item: SynapseEntityItem) => {
        setEditingItem(item);

        if (activeTab === 'users') {
            const user = item as SynapseUser;
            setFormState({
                id: user.id,
                name: user.name || '',
                email: user.email || '',
                partnerid: user.partnerid || '',
                profilepic: user.profilepic || DEFAULT_PROFILE_PIC,
                password: '',
                teams: asTeamRefs(user.teams).map(team => team.id),
            });
        } else if (activeTab === 'projects') {
            const project = item as SynapseProject;
            setFormState({
                id: project.id,
                name: project.name || '',
                description: project.description || '',
                bannerurl: project.bannerurl || '',
                teams: asTeamRefs(project.teams).map(team => team.id),
            });
        } else if (activeTab === 'teams') {
            const team = item as SynapseTeam;
            setFormState({
                id: team.id,
                name: team.name || '',
                partnerid: team.partnerid || '',
                logourl: team.logourl || '',
            });
        } else if (activeTab === 'partners') {
            const partner = item as SynapsePartner;
            setFormState({
                id: partner.id,
                name: partner.name || '',
                logourl: partner.logourl || '',
            });
        } else {
            const session = item as SynapseSession;
            setFormState({
                prim: session.prim,
                project_id: session.project_id || '',
                session_id: session.session_id || '',
            });
        }

        setShowFormModal(true);
        setError('');
        setSuccess('');
    };

    const openDeleteModal = (item: SynapseEntityItem) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
        setError('');
        setSuccess('');
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
    ) => {
        const { name, value, multiple, options } =
            e.target as HTMLSelectElement;

        if (multiple) {
            const selected = Array.from(options)
                .filter(option => option.selected)
                .map(option => option.value);

            setFormState(prev => ({ ...prev, [name]: selected }));
            return;
        }

        setFormState(prev => {
            const next: FormState = { ...prev, [name]: value };

            if (activeTab === 'users' && name === 'partnerid') {
                next.teams = [];
            }

            return next;
        });
    };

    const validateForm = () => {
        if (activeTab === 'users' || activeTab === 'teams') {
            if (!formState.partnerid) {
                return 'Partner is required.';
            }
        }

        if (activeTab === 'sessions') {
            if (!formState.project_id) return 'Project is required.';
            if (!formState.session_id) return 'Session is required.';
        }

        return '';
    };

    const buildPayload = () => {
        if (activeTab === 'partners') {
            return {
                ...(editingItem ? { id: formState.id } : {}),
                name: (formState.name || '').trim(),
                logourl: (formState.logourl || '').trim(),
            };
        }

        if (activeTab === 'teams') {
            return {
                ...(editingItem ? { id: formState.id } : {}),
                name: (formState.name || '').trim(),
                partnerid: formState.partnerid,
                logourl: (formState.logourl || '').trim(),
            };
        }

        if (activeTab === 'projects') {
            return {
                ...(editingItem ? { id: formState.id } : {}),
                name: (formState.name || '').trim(),
                description: (formState.description || '').trim(),
                bannerurl: (formState.bannerurl || '').trim(),
                teams: asArray<string>(formState.teams).map(id => ({ id })),
            };
        }

        if (activeTab === 'sessions') {
            return {
                ...(editingItem ? { prim: Number(formState.prim) } : {}),
                project_id: formState.project_id,
                session_id: (formState.session_id || '').trim(),
            };
        }

        return {
            ...(editingItem ? { id: formState.id } : {}),
            name: (formState.name || '').trim(),
            email: (formState.email || '').trim(),
            partnerid: formState.partnerid,
            profilepic:
                (formState.profilepic || '').trim() || DEFAULT_PROFILE_PIC,
            ...(formState.password?.trim()
                ? { password: formState.password.trim() }
                : {}),
            teams: asArray<string>(formState.teams).map(id => ({ id })),
        };
    };

    const getDeletePayload = () => {
        if (activeTab === 'sessions') {
            const session = itemToDelete as SynapseSession | null;
            return { prim: session?.prim };
        }

        const item = itemToDelete as
            | SynapsePartner
            | SynapseTeam
            | SynapseProject
            | SynapseUser
            | null;
        return { id: item?.id };
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const payload = buildPayload();

            if (editingItem) {
                await updateSynapseItem(activeTab, payload);
                setSuccess(
                    `${titleCase(activeTab.slice(0, -1))} updated successfully.`,
                );
            } else {
                await createSynapseItem(activeTab, payload);
                setSuccess(
                    `${titleCase(activeTab.slice(0, -1))} created successfully.`,
                );
            }

            setShowFormModal(false);
            await fetchOverview();
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : `Failed to save ${activeTab.slice(0, -1)}`,
            );
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            await deleteSynapseItem(activeTab, getDeletePayload());
            setShowDeleteModal(false);
            setItemToDelete(null);
            setSuccess(
                `${titleCase(activeTab.slice(0, -1))} deleted successfully.`,
            );
            await fetchOverview();
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : `Failed to delete ${activeTab.slice(0, -1)}`,
            );
        } finally {
            setActionLoading(false);
        }
    };

    const renderSummary = () => (
        <div className="d-flex flex-wrap gap-3 mb-4">
            {TAB_LABELS.map(tab => (
                <div
                    key={tab.key}
                    className="border rounded px-3 py-2 bg-white"
                    style={{ minWidth: '140px' }}
                >
                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {tab.label}
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>
                        {data[tab.key].length}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRows = () => {
        if (activeTab === 'users') {
            return filteredUsers.map(user => (
                <tr key={user.id}>
                    <td>{user.name || '-'}</td>
                    <td>{user.email || '-'}</td>
                    <td>{user.partner?.name || '-'}</td>
                    <td>
                        {asTeamRefs(user.teams).length ? (
                            asTeamRefs(user.teams).map(team => (
                                <Badge
                                    key={team.id}
                                    bg="secondary"
                                    className="me-1"
                                >
                                    {team.name}
                                </Badge>
                            ))
                        ) : (
                            '-'
                        )}
                    </td>
                    <td>
                        <Thumb
                            src={user.profilepic}
                            alt={user.name || 'profile'}
                        />
                    </td>
                    <td className="text-end">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(user)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(user)}
                        >
                            Delete
                        </Button>
                    </td>
                </tr>
            ));
        }

        if (activeTab === 'teams') {
            return filteredTeams.map(team => (
                <tr key={team.id}>
                    <td>{team.name || '-'}</td>
                    <td>{team.partner_name || '-'}</td>
                    <td>
                        <Thumb src={team.logourl} alt={team.name || 'logo'} />
                    </td>
                    <td className="text-end">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(team)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(team)}
                        >
                            Delete
                        </Button>
                    </td>
                </tr>
            ));
        }

        if (activeTab === 'projects') {
            return filteredProjects.map(project => (
                <tr key={project.id}>
                    <td>{project.name || '-'}</td>
                    <td>
                        {asTeamRefs(project.teams).length ? (
                            asTeamRefs(project.teams).map(team => (
                                <Badge
                                    key={team.id}
                                    bg="secondary"
                                    className="me-1"
                                >
                                    {team.name || team.id}
                                </Badge>
                            ))
                        ) : (
                            '-'
                        )}
                    </td>
                    <td>
                        <Thumb
                            src={project.bannerurl}
                            alt={project.name || 'banner'}
                            width={80}
                            height={40}
                        />
                    </td>
                    <td className="text-end">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(project)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(project)}
                        >
                            Delete
                        </Button>
                    </td>
                </tr>
            ));
        }

        if (activeTab === 'sessions') {
            return filteredSessions.map(session => (
                <tr key={session.prim}>
                    <td>{session.project_name || '-'}</td>
                    <td>{session.session_name || session.session_id}</td>
                    <td>{session.session_status || '-'}</td>
                    <td>{formatDate(session.startdate)}</td>
                    <td className="text-end">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(session)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(session)}
                        >
                            Delete
                        </Button>
                    </td>
                </tr>
            ));
        }

        return data.partners.map(partner => (
            <tr key={partner.id}>
                <td>{partner.name || '-'}</td>
                <td>
                    <Thumb
                        src={partner.logourl}
                        alt={partner.name || 'logo'}
                    />
                </td>
                <td className="text-end">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => openEditModal(partner)}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDeleteModal(partner)}
                    >
                        Delete
                    </Button>
                </td>
            </tr>
        ));
    };

    const renderHeaders = () => {
        if (activeTab === 'users') {
            return (
                <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Partner</th>
                    <th>Teams</th>
                    <th>Profile Pic</th>
                    <th className="text-end">Actions</th>
                </tr>
            );
        }

        if (activeTab === 'teams') {
            return (
                <tr>
                    <th>Name</th>
                    <th>Partner</th>
                    <th>Logo</th>
                    <th className="text-end">Actions</th>
                </tr>
            );
        }

        if (activeTab === 'projects') {
            return (
                <tr>
                    <th>Name</th>
                    <th>Teams</th>
                    <th>Banner</th>
                    <th className="text-end">Actions</th>
                </tr>
            );
        }

        if (activeTab === 'sessions') {
            return (
                <tr>
                    <th>Project</th>
                    <th>Session</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th className="text-end">Actions</th>
                </tr>
            );
        }

        return (
            <tr>
                <th>Name</th>
                <th>Logo</th>
                <th className="text-end">Actions</th>
            </tr>
        );
    };

    const renderFormFields = () => {
        if (activeTab === 'partners') {
            return (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            name="name"
                            value={formState.name || ''}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Logo URL</Form.Label>
                        <Form.Control
                            name="logourl"
                            value={formState.logourl || ''}
                            onChange={handleChange}
                        />
                        <ImagePreview src={formState.logourl} label="Logo" />
                    </Form.Group>
                </>
            );
        }

        if (activeTab === 'teams') {
            return (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            name="name"
                            value={formState.name || ''}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Partner <span style={{ color: '#dc3545' }}>*</span>
                        </Form.Label>
                        <Form.Select
                            name="partnerid"
                            value={formState.partnerid || ''}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select partner</option>
                            {partnerOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Logo URL</Form.Label>
                        <Form.Control
                            name="logourl"
                            value={formState.logourl || ''}
                            onChange={handleChange}
                        />
                        <ImagePreview src={formState.logourl} label="Logo" />
                    </Form.Group>
                </>
            );
        }

        if (activeTab === 'projects') {
            return (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            name="name"
                            value={formState.name || ''}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Banner URL</Form.Label>
                        <Form.Control
                            name="bannerurl"
                            value={formState.bannerurl || ''}
                            onChange={handleChange}
                        />
                        <ImagePreview
                            src={formState.bannerurl}
                            label="Banner"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Teams</Form.Label>
                        <Form.Select
                            multiple
                            name="teams"
                            value={formState.teams || []}
                            onChange={handleChange}
                            style={{ minHeight: '140px' }}
                        >
                            {teamOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text>Select one or more teams.</Form.Text>
                    </Form.Group>
                </>
            );
        }

        if (activeTab === 'sessions') {
            const selectedDiscussion =
                discussionOptions.find(
                    option => option.value === formState.session_id,
                ) || null;

            return (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Project</Form.Label>
                        <Form.Select
                            name="project_id"
                            value={formState.project_id || ''}
                            onChange={handleChange}
                        >
                            <option value="">Select project</option>
                            {projectOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Session</Form.Label>
                        <Select
                            isClearable
                            isSearchable
                            isLoading={discussionsLoading}
                            options={discussionOptions}
                            value={selectedDiscussion}
                            placeholder="Search active discussions..."
                            onChange={option =>
                                setFormState(prev => ({
                                    ...prev,
                                    session_id: option?.value || '',
                                }))
                            }
                            noOptionsMessage={() =>
                                discussionsLoading
                                    ? 'Loading discussions...'
                                    : 'No active discussions found'
                            }
                        />
                        <Form.Text>
                            Only active discussions are listed. Search by name.
                        </Form.Text>
                    </Form.Group>
                </>
            );
        }

        return (
            <>
                <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        name="name"
                        value={formState.name || ''}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                        name="email"
                        type="email"
                        value={formState.email || ''}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Partner <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Select
                        name="partnerid"
                        value={formState.partnerid || ''}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select partner</option>
                        {partnerOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Teams</Form.Label>
                    <Form.Select
                        multiple
                        name="teams"
                        value={formState.teams || []}
                        onChange={handleChange}
                        style={{ minHeight: '140px' }}
                    >
                        {filteredTeamOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text>
                        Teams are filtered by the selected partner.
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Profile Pic URL</Form.Label>
                    <Form.Control
                        name="profilepic"
                        value={formState.profilepic || ''}
                        onChange={handleChange}
                    />
                    <ImagePreview
                        src={formState.profilepic}
                        label="Profile pic"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>
                        Password{' '}
                        {editingItem ? '(leave blank to keep existing)' : ''}
                    </Form.Label>
                    <Form.Control
                        name="password"
                        type="password"
                        value={formState.password || ''}
                        onChange={handleChange}
                    />
                </Form.Group>
            </>
        );
    };

    return (
        <div
            style={{
                width: '100%',
                alignSelf: 'flex-start',
                padding: '20px',
                paddingBottom: '180px',
                boxSizing: 'border-box',
            }}
        >
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h2 style={{ marginBottom: '6px', color: '#000' }}>
                        Synapse Management
                    </h2>
                    <div style={{ color: '#6c757d' }}>
                        Manage users, teams, projects, session mappings, and
                        partners.
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={fetchOverview}>
                        Refresh
                    </Button>
                    <Button onClick={openCreateModal}>
                        Add {titleCase(activeTab.slice(0, -1))}
                    </Button>
                </div>
            </div>

            {renderSummary()}

            {success && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccess('')}
                >
                    {success}
                </Alert>
            )}

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            <Nav
                variant="tabs"
                activeKey={activeTab}
                onSelect={key => {
                    if (!key) return;
                    setActiveTab(key as EntityKey);
                    resetFilters();
                }}
                className="mb-3"
            >
                {TAB_LABELS.map(tab => (
                    <Nav.Item key={tab.key}>
                        <Nav.Link eventKey={tab.key}>
                            {tab.label}{' '}
                            <Badge bg="secondary">{data[tab.key].length}</Badge>
                        </Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {activeTab !== 'partners' && (
                <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                    {(activeTab === 'users' || activeTab === 'teams') && (
                        <Form.Group style={{ minWidth: '220px' }}>
                            <Form.Label className="mb-1">
                                Filter by partner
                            </Form.Label>
                            <Form.Select
                                value={filterPartnerId}
                                onChange={e => {
                                    setFilterPartnerId(e.target.value);
                                    setFilterTeamId('');
                                }}
                            >
                                <option value="">All partners</option>
                                {partnerOptions.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {(activeTab === 'users' || activeTab === 'projects') && (
                        <Form.Group style={{ minWidth: '220px' }}>
                            <Form.Label className="mb-1">
                                Filter by team
                            </Form.Label>
                            <Form.Select
                                value={filterTeamId}
                                onChange={e => setFilterTeamId(e.target.value)}
                            >
                                <option value="">All teams</option>
                                {(activeTab === 'users'
                                    ? filterTeamOptions
                                    : teamOptions
                                ).map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {activeTab === 'sessions' && (
                        <Form.Group style={{ minWidth: '220px' }}>
                            <Form.Label className="mb-1">
                                Filter by project
                            </Form.Label>
                            <Form.Select
                                value={filterProjectId}
                                onChange={e =>
                                    setFilterProjectId(e.target.value)
                                }
                            >
                                <option value="">All projects</option>
                                {projectOptions.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {hasActiveFilters && (
                        <Button
                            variant="outline-secondary"
                            onClick={resetFilters}
                        >
                            Clear filters
                        </Button>
                    )}

                    <div
                        className="text-muted"
                        style={{ fontSize: '0.9rem', paddingBottom: '8px' }}
                    >
                        Showing {filteredRowsCount} of {data[activeTab].length}
                    </div>
                </div>
            )}

            <div
                className="bg-white border rounded p-3"
                style={{ marginBottom: '160px' }}
            >
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table responsive striped bordered hover>
                        <thead>{renderHeaders()}</thead>
                        <tbody>
                            {filteredRowsCount ? (
                                renderRows()
                            ) : (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-center text-muted py-4"
                                    >
                                        No {activeTab} found
                                        {hasActiveFilters
                                            ? ' for the selected filters'
                                            : ''}
                                        .
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </div>

            <Modal
                show={showFormModal}
                onHide={() => setShowFormModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingItem ? 'Edit' : 'Add'}{' '}
                        {titleCase(activeTab.slice(0, -1))}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>{renderFormFields()}</Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowFormModal(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={actionLoading}>
                        {actionLoading ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Delete {titleCase(activeTab.slice(0, -1))}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this {activeTab.slice(0, -1)}
                    ?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SynapseManagement;
